import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import Fviewer from '../core/Fviewer';
import Header from './components/Header';
import { parseFile, detectFileType } from '../core/utils/fileParser';
import type {
  AppTheme,
  Annotation,
  MDAnnotation,
  TableAnnotation,
  ToolsConfig,
} from '../core/types';
import type { ParseResult } from '../core/utils/fileParser';

// StaticFileReader组件属性接口
export interface StaticFileReaderProps {
  // 文件相关（data 直传时可不提供 fileUrl）
  fileUrl?: string;
  fileType?: string;
  fileName?: string;
  token?: string;
  initialPage?: number;
  annotations?: (Annotation | MDAnnotation | TableAnnotation)[];
  // 直接数据传递
  data?: any;
  // 事件回调
  eventsEmit?: (name: string, data?: any, innerFn?: any) => void;

  // 样式配置
  styles?: {
    theme?: AppTheme;
    mode?: 'light' | 'dark';
  };

  // 工具配置
  tools?: ToolsConfig;

  // 自定义组件
  customComponents?: {
    LoadingComponent?: React.ComponentType<{
      status: string;
      theme: AppTheme;
    }>;
    ErrorComponent?: React.ComponentType<{
      error: string;
      theme: AppTheme;
    }>;
  };

  // 自定义类名
  className?: string;
  headerClass?: string;
  contentClass?: string;
}

// 样式组件
const ReaderContainer = styled.div<{ $theme: AppTheme }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: ${props => props.$theme.colors.background};
  border: 1px solid ${props => props.$theme.colors.border};
  border-radius: ${props => props.$theme.space.radius};
  overflow: hidden;
  min-height: 0; /* 确保flex子元素可以收缩 */
`;

/**
 * 默认文件加载动画组件
 * 提供美观的文件加载动画效果，支持状态文本显示
 */
const DefaultLoadingComponent: React.FC<{
  status: string;
  theme: AppTheme;
}> = ({ status, theme }) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: '40px',
        background: theme.colors.background,
      }}
    >
      {/* 文件图标动画 */}
      <div
        style={{
          position: 'relative',
          width: '80px',
          height: '80px',
          marginBottom: '24px',
        }}
      >
        {/* 文件图标背景 */}
        <div
          style={{
            width: '60px',
            height: '80px',
            background: theme.colors.primary,
            borderRadius: '8px 8px 0 0',
            position: 'relative',
            margin: '0 auto',
          }}
        >
          {/* 文件折角 */}
          <div
            style={{
              position: 'absolute',
              top: '0',
              right: '0',
              width: '0',
              height: '0',
              borderStyle: 'solid',
              borderWidth: '0 20px 20px 0',
              borderColor: `transparent ${theme.colors.background} transparent transparent`,
            }}
          />
        </div>

        {/* 加载动画圆点 */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: theme.colors.background,
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      </div>

      {/* 状态文本 */}
      <div
        style={{
          fontSize: '16px',
          fontWeight: '500',
          color: theme.colors.text,
          marginBottom: '8px',
          textAlign: 'center',
        }}
      >
        {status}
      </div>

      {/* 进度指示器 */}
      <div
        style={{
          width: '200px',
          height: '4px',
          background: theme.colors.border,
          borderRadius: '2px',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            width: '30%',
            height: '100%',
            background: theme.colors.primary,
            borderRadius: '2px',
            animation: 'loading-progress 2s ease-in-out infinite',
          }}
        />
      </div>

      {/* 动画样式 */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            opacity: 0.5;
            transform: translate(-50%, -50%) scale(1.1);
          }
        }

        @keyframes loading-progress {
          0% {
            transform: translateX(-100%);
          }
          50% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>
  );
};

const LoadingContainer = styled.div<{ $theme: AppTheme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${props => props.$theme.colors.disabledText};
  font-size: 14px;
`;

const ErrorContainer = styled.div<{ $theme: AppTheme }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${props => props.$theme.colors.error};
  text-align: center;
  padding: 20px;
`;

// 默认主题
const defaultTheme: AppTheme = {
  colors: {
    primary: '#007bff',
    secondary: '#6c757d',
    success: '#008000',
    warning: '#FFA500',
    error: '#FF0000',
    info: '#0000FF',
    background: '#f8f9fa',
    text: '#343a40',
    border: '#dee2e6',
    disabled: '#808080',
    disabledBackground: '#F5F5F5',
    disabledText: '#808080',
  },
  space: {
    sidebar: 'md',
    size: 'md',
    radius: 'md',
    padding: 'md',
    margin: 'md',
    shadow: 'md',
    lineHeight: 'md',
  },
};

/**
 * StaticFileReader组件
 * 静态文件读取器，支持从URL加载文件并解析显示
 *
 * @param props - 组件属性
 * @returns StaticFileReader组件
 */
const StaticFileReader: React.FC<StaticFileReaderProps> = ({
  fileUrl,
  fileType,
  annotations,
  fileName,
  token,
  initialPage = 1,
  data,
  eventsEmit = () => {},
  styles,
  tools = {
    annotation: false,
    download: false,
    zoom: true,
    close: true,
    navigation: true,
  },
  customComponents,
  className,
  headerClass,
  contentClass,
}) => {
  // 使用主题
  const mergedTheme = styles?.theme || defaultTheme;

  // 状态管理
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState<string>('文件下载中...');
  const [error, setError] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0); // 图片旋转角度
  const [isAnnotating, setIsAnnotating] = useState(false);
  //const [annotations, setAnnotations] = useState<Annotation[]>([]);

  /**
   * 重置所有状态
   */
  const resetState = useCallback(() => {
    setIsLoading(true);
    setLoadingStatus('文件下载中...');
    setError(null);
    setParseResult(null);
    setCurrentPage(initialPage);
    setRotation(0);
    setIsAnnotating(false);
  }, [initialPage]);

  /**
   * 加载并解析文件
   */
  const loadFile = useCallback(async () => {
    console.log('=== loadFile 开始 ===');
    console.log('当前参数:', { fileUrl, fileName, fileType, data });

    // 重置所有状态
    resetState();

    // 如果有直接传递的数据，直接使用
    if (data) {
      setIsLoading(false);
      setError(null);
      setParseResult({
        type: data.fileType || fileType || 'text',
        content: data.content,
        fileName: data.fileName || fileName,
        fileType: data.fileType || fileType,
        totalPages: data.totalPages || 1,
      });
      return;
    }

    if (!fileUrl) {
      setError('文件URL不能为空');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // 根据文件类型设置不同的加载状态
      const detectedType = detectFileType(fileName, fileType);
      if (detectedType === 'image') {
        setLoadingStatus('图片下载中...');
      } else if (detectedType === 'pdf') {
        setLoadingStatus('PDF文件下载中...');
      } else if (detectedType === 'markdown' || detectedType === 'markdown_table') {
        setLoadingStatus('Markdown文件下载中...');
      } else {
        setLoadingStatus('文件下载中...');
      }

      // 使用core层的解析函数
      const result = await parseFile(fileUrl, {
        fileName,
        fileType,
        token,
      });

      console.log('=== StaticFileReader parseFile 结果 ===');
      console.log('result:', result);
      console.log('result.type:', result.type);
      console.log('result.content:', result.content);
      console.log('result.content?.text:', result.content?.text);
      console.log('result.fileName:', result.fileName);
      console.log('result.fileType:', result.fileType);

      if (result.error) {
        setError(result.error);
      } else {
        // 根据文件类型设置不同的完成状态
        if (result.type === 'image') {
          setLoadingStatus('图片加载完成');
        } else if (result.type === 'pdf') {
          setLoadingStatus('PDF加载完成');
        } else if (result.type === 'markdown' || result.type === 'markdown_table') {
          setLoadingStatus('Markdown加载完成');
        } else {
          setLoadingStatus('文件加载完成');
        }

        console.log('=== parseResult 设置 ===');
        console.log('parseResult:', result);
        console.log('parseResult.content:', result.content);
        console.log('parseResult.content.document:', result.content?.document);
        setParseResult(result);
      }
    } catch (err) {
      setError(`文件加载失败: ${err instanceof Error ? err.message : '未知错误'}`);
    } finally {
      setIsLoading(false);
    }
  }, [fileUrl, fileName, fileType, token, data, resetState]);

  // 监听文件URL变化
  useEffect(() => {
    loadFile();
  }, [loadFile]);

  /**
   * 处理事件回调
   */
  const handleEventsEmit = useCallback(
    (name: string, data?: any, innerFn?: any) => {
      // 处理内部事件
      switch (name) {
        case 'pageChange':
          if (data?.pageNumber) {
            setCurrentPage(data.pageNumber);
          }
          break;

        // if (data?.annotationId) {
        //   setAnnotations(prev => prev.filter(a => a.id !== data.annotationId));
        // }
        // break;
        default:
          // 传递给外部事件处理器
          eventsEmit(name, data, innerFn);
          break;
      }
    },
    [eventsEmit]
  );

  /**
   * 处理缩放放大
   */
  const handleZoomIn = useCallback(() => {
    const newScale = Math.min(3, scale + 0.1);
    setScale(newScale);
    handleEventsEmit('zoom:change', { scale: newScale });
  }, [scale, handleEventsEmit]);

  /**
   * 处理缩放缩小
   */
  const handleZoomOut = useCallback(() => {
    const newScale = Math.max(0.3, scale - 0.1);
    setScale(newScale);
    handleEventsEmit('zoom:change', { scale: newScale });
  }, [scale, handleEventsEmit]);

  /**
   * 处理上一页
   */
  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      handleEventsEmit('page:change', { page: newPage });
    }
  }, [currentPage, handleEventsEmit]);

  /**
   * 处理下一页
   */
  const handleNextPage = useCallback(() => {
    const totalPages = parseResult?.totalPages || 0;
    if (totalPages && currentPage < totalPages) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      handleEventsEmit('page:change', { page: newPage });
    }
  }, [currentPage, parseResult?.totalPages, handleEventsEmit]);

  /**
   * 处理标注切换
   */
  const handleAnnotationToggle = useCallback(() => {
    const newAnnotating = !isAnnotating;
    setIsAnnotating(newAnnotating);
    handleEventsEmit('annotation:toggle', { isAnnotating: newAnnotating });
  }, [isAnnotating, handleEventsEmit]);

  /**
   * 处理下载
   */
  const handleDownload = useCallback(() => {
    handleEventsEmit('download:start', { fileUrl, fileName });
  }, [handleEventsEmit, fileUrl, fileName]);

  /**
   * 处理向左旋转
   */
  const handleRotateLeft = useCallback(() => {
    setRotation(prev => (prev - 90) % 360);
    handleEventsEmit('rotate:left', { rotation: (rotation - 90) % 360 });
  }, [rotation, handleEventsEmit]);

  /**
   * 处理向右旋转
   */
  const handleRotateRight = useCallback(() => {
    setRotation(prev => (prev + 90) % 360);
    handleEventsEmit('rotate:right', { rotation: (rotation + 90) % 360 });
  }, [rotation, handleEventsEmit]);

  /**
   * 处理关闭
   */
  const handleClose = useCallback(() => {
    handleEventsEmit('viewer:close');
  }, [handleEventsEmit]);

  /**
   * 根据文件类型动态控制工具按钮
   */
  const getDynamicTools = useCallback((fileType: string, baseTools: ToolsConfig): ToolsConfig => {
    const isMultiPageFile = fileType === 'pdf'; // 只有 PDF 是多页文件
    const isTextBasedFile =
      fileType === 'text' ||
      fileType === 'markdown' ||
      fileType === 'markdown_table' ||
      fileType === 'html';

    return {
      ...baseTools,
      // 缩放功能：只有 PDF 和图片支持
      zoom: baseTools.zoom && !isTextBasedFile,
      // 导航功能：只有多页文件支持（PDF）
      navigation: baseTools.navigation && isMultiPageFile,
      // 标注功能：PDF、Markdown、Markdown表格都支持
      annotation:
        baseTools.annotation &&
        (fileType === 'pdf' || fileType === 'markdown' || fileType === 'markdown_table'),
      // 旋转功能：只有图片支持
      rotate: baseTools.rotate && fileType === 'image',
      // 下载功能：所有文件都支持
      download: baseTools.download,
      // 关闭功能：所有文件都支持
      close: baseTools.close,
    };
  }, []);

  // 渲染加载状态
  if (isLoading) {
    const loadingTools = getDynamicTools(fileType || 'unknown', {
      ...tools,
      navigation: false,
    });

    return (
      <ReaderContainer $theme={mergedTheme} className={className}>
        {/* 头部工具栏 - 始终显示 */}
        <Header
          fileName={fileName || '加载中...'}
          currentPage={1}
          totalPage={0}
          tools={loadingTools}
          styles={styles}
          onPrevPage={() => {}}
          onNextPage={() => {}}
          onZoomIn={() => {}}
          onZoomOut={() => {}}
          onAnnotationToggle={() => {}}
          onDownload={() => {}}
          onClose={handleClose}
          scale={1}
          isAnnotating={false}
          className={headerClass}
        />

        {/* 内容区域 - 显示加载状态 */}
        {customComponents?.LoadingComponent ? (
          <customComponents.LoadingComponent status={loadingStatus} theme={mergedTheme} />
        ) : (
          <DefaultLoadingComponent status={loadingStatus} theme={mergedTheme} />
        )}
      </ReaderContainer>
    );
  }

  // 渲染错误状态
  if (error) {
    const errorTools = getDynamicTools(fileType || 'unknown', {
      ...tools,
      navigation: false,
    });

    return (
      <ReaderContainer $theme={mergedTheme} className={className}>
        {/* 头部工具栏 - 始终显示 */}
        <Header
          fileName={fileName || '错误'}
          currentPage={1}
          totalPage={0}
          tools={errorTools}
          styles={styles}
          onPrevPage={() => {}}
          onNextPage={() => {}}
          onZoomIn={() => {}}
          onZoomOut={() => {}}
          onAnnotationToggle={() => {}}
          onDownload={() => {}}
          onClose={handleClose}
          scale={1}
          isAnnotating={false}
          className={headerClass}
        />

        {/* 内容区域 - 显示错误状态 */}
        <ErrorContainer $theme={mergedTheme}>
          <div>文件加载失败</div>
          <div style={{ fontSize: '12px', marginTop: '8px' }}>{error}</div>
        </ErrorContainer>
      </ReaderContainer>
    );
  }

  // 渲染文件内容
  if (!parseResult) {
    const unknownTools = getDynamicTools(fileType || 'unknown', {
      ...tools,
      navigation: false,
    });

    return (
      <ReaderContainer $theme={mergedTheme} className={className}>
        {/* 头部工具栏 - 始终显示 */}
        <Header
          fileName={fileName || '未知文件'}
          currentPage={1}
          totalPage={0}
          tools={unknownTools}
          styles={styles}
          onPrevPage={() => {}}
          onNextPage={() => {}}
          onZoomIn={() => {}}
          onZoomOut={() => {}}
          onAnnotationToggle={() => {}}
          onDownload={() => {}}
          onClose={handleClose}
          scale={1}
          isAnnotating={false}
          className={headerClass}
        />

        {/* 内容区域 - 显示解析错误 */}
        <ErrorContainer $theme={mergedTheme}>
          <div>无法解析文件</div>
        </ErrorContainer>
      </ReaderContainer>
    );
  }

  // 准备传递给Fviewer的数据
  const viewerData = {
    content: parseResult.content,
    ...((parseResult.fileName || fileName) && { fileName: parseResult.fileName || fileName }),
    ...((parseResult.fileType || fileType) && { fileType: parseResult.fileType || fileType }),
    ...(parseResult.totalPages && { totalPages: parseResult.totalPages }),
  };

  // 根据文件类型动态控制工具按钮
  const dynamicTools = getDynamicTools(parseResult.type, tools);

  console.log('=== viewerData 准备 ===');
  console.log('viewerData:', viewerData);
  console.log('viewerData.content:', viewerData.content);
  console.log('viewerData.content.document:', viewerData.content?.document);
  console.log('文件类型:', parseResult.type);
  console.log('动态工具配置:', dynamicTools);
  console.log('=== 标注数据传递 ===');
  console.log('annotations prop:', annotations);
  console.log('annotations 长度:', annotations?.length || 0);

  return (
    <ReaderContainer $theme={mergedTheme} className={className}>
      {/* 头部工具栏 */}
      <Header
        fileName={viewerData.fileName || parseResult.fileName || fileName || '未知文件'}
        currentPage={parseResult.type === 'pdf' ? currentPage : 1}
        totalPage={parseResult.type === 'pdf' ? parseResult.totalPages || 0 : 0}
        tools={dynamicTools}
        styles={styles}
        onPrevPage={parseResult.type === 'pdf' ? handlePrevPage : () => {}}
        onNextPage={parseResult.type === 'pdf' ? handleNextPage : () => {}}
        onZoomIn={dynamicTools.zoom ? handleZoomIn : () => {}}
        onZoomOut={dynamicTools.zoom ? handleZoomOut : () => {}}
        onAnnotationToggle={dynamicTools.annotation ? handleAnnotationToggle : () => {}}
        onDownload={dynamicTools.download ? handleDownload : () => {}}
        onRotateLeft={dynamicTools.rotate ? handleRotateLeft : () => {}}
        onRotateRight={dynamicTools.rotate ? handleRotateRight : () => {}}
        onClose={handleClose}
        scale={scale}
        isAnnotating={isAnnotating}
        className={headerClass}
      />

      {/* 内容区域 */}
      <Fviewer
        data={viewerData}
        annotationData={annotations || []}
        totalPage={parseResult.type === 'pdf' ? parseResult.totalPages || 0 : 0}
        currentPage={parseResult.type === 'pdf' ? currentPage : 1}
        scale={scale}
        rotation={rotation}
        eventsEmit={handleEventsEmit}
        styles={styles}
        tools={dynamicTools}
        className={contentClass}
        setScale={setScale}
      />
    </ReaderContainer>
  );
};

export default StaticFileReader;
